<!--
  Conditional render container for `sbi.branch.if` and `sbi.branch.when`.

  Renders the chosen arm's children (forwarded through InsightNode), a
  skeleton while the test query is in flight, and an error banner if the
  test marker rejected. The `whenLabel` footnote is always rendered —
  it's the viewer-facing safety net that distinguishes "this section is
  intentionally hidden" from "this section is broken" (see briefing.md
  → "Branching with `sbi.branch`").

  Embedder theming:
    - data-part="branch-container" / "branch-loading" / "branch-error" /
      "branch-when-label" — shadow-DOM-safe targeting hooks.
    - --lucent-branch-bg / --lucent-branch-loading-bg / --lucent-branch-rule /
      --lucent-branch-when-label / --lucent-branch-error-bg /
      --lucent-branch-error-color — the supported customisation surface.
-->

<script setup lang="ts">
import { computed } from "vue";
import type { ChangeValue } from "../sbi/protocol";
import type { TreeNode } from "../sbi/validate-render-op";
import InsightNode from "./InsightNode.vue";
import { formatError } from "./errorText.ts";

const props = defineProps<{
  /**
   * The if-frame node. `arm` carries the resolved branch; `errored` flips
   * the body to the banner; `whenLabel` is the required footnote.
   */
  node: TreeNode & { kind: "if" };
  /** Pass-through pass-rerunning flag from EmbedHost. */
  loading?: boolean;
  /** Per-pass field → format registry forwarded to children. */
  formats?: Record<string, string>;
}>();

const headlineText = computed(() =>
  formatError("Couldn't load this section.", props.node.exId),
);

defineEmits<{
  (e: "click-target", id: string, datum?: Record<string, unknown>, rowIndex?: number): void;
  (e: "change-target", id: string, value: ChangeValue): void;
  /** Forwarded from a descendant modal drilldown's X / Esc / backdrop. */
  (e: "chrome-click-target", id: string): void;
  /** Forwarded from descendant accordions so EmbedHost can surface the DOM event. */
  (e: "accordion-toggle", summary: string, open: boolean): void;
  /** Forwarded from descendant callouts so EmbedHost can surface the DOM event. */
  (e: "callout-rendered", kind: string, title?: string): void;
}>();
</script>

<template>
  <div
    class="branch"
    data-part="branch-container"
    :data-arm="node.arm"
    :data-loading="node.loading ? 'true' : 'false'"
    :data-errored="node.errored ? 'true' : 'false'"
    :aria-busy="node.loading ? 'true' : 'false'"
  >
    <!--
      Phase 1 (test in flight). A subtle pulsing surface tells viewers
      "the data behind this section is loading". Once the test resolves
      and `loading` flips false, this disappears and the chosen arm's
      children land in its place — usually before the user notices.
    -->
    <div
      v-if="node.loading"
      class="branch-loading"
      data-part="branch-loading"
    >
      <span class="visually-hidden">Loading conditional section…</span>
    </div>

    <!--
      Phase 2, errored. The test marker rejected — render a "couldn't
      load" banner in place of the children. Sibling sections render
      normally; the host also fires a recoverable `lucent-error` event
      so embedders can react (retry, hide, log).
    -->
    <div
      v-else-if="node.errored"
      class="branch-error"
      data-part="branch-error"
      role="status"
    >
      <p class="branch-error-headline">{{ headlineText }}</p>
      <p v-if="node.errorMessage" class="branch-error-detail">
        {{ node.errorMessage }}
      </p>
    </div>

    <!--
      Phase 2, resolved. Render the chosen arm's children. When `arm`
      is `"else"` and there are no else children (e.g. a `when` whose
      test resolved false), this renders nothing — the footnote below
      tells viewers why.
    -->
    <template v-else>
      <InsightNode
        v-for="(child, i) in node.children"
        :key="i"
        :node="child"
        :loading="loading"
        :formats="formats"
        @click-target="(id, datum, rowIndex) => $emit('click-target', id, datum, rowIndex)"
        @change-target="(id, value) => $emit('change-target', id, value)"
        @chrome-click-target="(id) => $emit('chrome-click-target', id)"
        @accordion-toggle="(summary, open) => $emit('accordion-toggle', summary, open)"
        @callout-rendered="(kind, title) => $emit('callout-rendered', kind, title)"
      />
    </template>

    <!--
      The required viewer-facing footnote. Rendered on every state
      (loading / errored / resolved) so the section's purpose is always
      visible. GM persona's safety net: empty space and broken space
      look identical without it.
    -->
    <p
      class="branch-when-label"
      data-part="branch-when-label"
    >
      Showing when {{ node.whenLabel }}.
    </p>
  </div>
</template>

<style scoped>
.branch {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: var(--lucent-branch-bg, transparent);
  border-radius: var(--radius, 8px);
}

.branch-loading {
  min-height: 96px;
  border-radius: var(--radius, 8px);
  background: var(--lucent-branch-loading-bg, var(--rule-soft, rgba(0, 0, 0, 0.04)));
  animation: lucent-branch-pulse 1.4s ease-in-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .branch-loading {
    animation: none;
    opacity: 0.7;
  }
}
@keyframes lucent-branch-pulse {
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
}

.branch-error {
  padding: 12px 14px;
  border: 1px solid var(--lucent-branch-error-rule, var(--rule, #e5e7eb));
  border-radius: var(--radius, 8px);
  background: var(--lucent-branch-error-bg, rgba(176, 0, 32, 0.04));
  color: var(--lucent-branch-error-color, var(--lucent-error, #b00020));
}
.branch-error-headline {
  margin: 0;
  font-weight: 600;
  font-size: 14px;
}
.branch-error-detail {
  margin: 4px 0 0;
  font-size: 12px;
  opacity: 0.85;
}

.branch-when-label {
  margin: 0;
  font-size: 12px;
  color: var(--lucent-branch-when-label, var(--card-muted, #6b7280));
  font-style: italic;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
